// Flux du navigateur : Server-Sent Events lus par fetch + ReadableStream (comme le panneau de
// l'ancienne app Pirouette, browser-screencast-stream.ts), adresse appelee TELLE QUELLE depuis la
// racine de l'hote (jamais prefixee par le chemin /chat de la SPA). Le jeton est dans l'adresse :
// aucun en-tete d'autorisation.
//   data: {"type":"frame","data":"<jpeg base64>","metadata":{"deviceWidth":1280,"deviceHeight":950}}
//   data: {"type":"status","state":"active|idle","url":"…","title":"…"}

export interface ImageFlux {
  kind: "frame";
  data: string;
  metadata: { deviceWidth?: number; deviceHeight?: number };
}
export interface EtatFlux {
  kind: "status";
  state: "active" | "idle" | "unavailable";
  url?: string;
  title?: string;
}
export type MessageFlux = ImageFlux | EtatFlux;

const ETATS = new Set(["active", "idle", "unavailable"]);

function nombreOuRien(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : undefined;
}

function lireEvenement(bloc: string): MessageFlux | null {
  let donnees = "";
  for (const ligne of bloc.split("\n")) {
    if (ligne.startsWith("data:")) {
      donnees += ligne.slice(5).trim();
    }
  }
  if (!donnees) {
    return null;
  }
  let charge: unknown;
  try {
    charge = JSON.parse(donnees);
  } catch {
    return null;
  }
  if (typeof charge !== "object" || charge === null) {
    return null;
  }
  const c = charge as Record<string, unknown>;
  if (c.type === "frame" && typeof c.data === "string") {
    const meta =
      typeof c.metadata === "object" && c.metadata !== null
        ? (c.metadata as Record<string, unknown>)
        : {};
    const metadata: ImageFlux["metadata"] = {};
    const largeur = nombreOuRien(meta.deviceWidth);
    const hauteur = nombreOuRien(meta.deviceHeight);
    if (largeur) {
      metadata.deviceWidth = largeur;
    }
    if (hauteur) {
      metadata.deviceHeight = hauteur;
    }
    return { kind: "frame", data: c.data, metadata };
  }
  if (
    c.type === "status" &&
    typeof c.state === "string" &&
    ETATS.has(c.state)
  ) {
    const etat: EtatFlux = {
      kind: "status",
      state: c.state as EtatFlux["state"],
    };
    if (typeof c.url === "string") {
      etat.url = c.url;
    }
    if (typeof c.title === "string") {
      etat.title = c.title;
    }
    return etat;
  }
  return null;
}

/** Decoupe un tampon SSE en evenements complets ; le morceau inacheve est rendu dans `reste`. */
export function analyserTamponSse(tampon: string): {
  evenements: MessageFlux[];
  reste: string;
} {
  const normalise = tampon.replace(/\r\n/g, "\n");
  const parties = normalise.split("\n\n");
  const reste = parties.pop() ?? "";
  const evenements: MessageFlux[] = [];
  for (const bloc of parties) {
    const e = lireEvenement(bloc);
    if (e) {
      evenements.push(e);
    }
  }
  return { evenements, reste };
}

type Fetcher = (url: string, init?: RequestInit) => Promise<Response>;

/**
 * Etat de la session : vivante (200), terminee (401), ou inconnu (reseau, 5xx).
 * Le jeton expire au bout de 2 h ; c'est l'etat qui dit si un flux coupe est une fin.
 */
export async function lireEtatNavigateur(
  etatUrl: string,
  fetcher: Fetcher = fetch,
  signal?: AbortSignal
): Promise<"vivante" | "terminee" | "inconnu"> {
  try {
    const res = await fetcher(etatUrl, { signal, cache: "no-store" });
    if (res.status === 401 || res.status === 403) {
      return "terminee";
    }
    return res.ok ? "vivante" : "inconnu";
  } catch {
    return "inconnu";
  }
}

export interface OptionsFlux {
  streamUrl: string;
  etatUrl: string;
  surMessage: (m: MessageFlux) => void;
  /** Jeton expire (401) ou session fermee : plus de reconnexion. */
  surTerminee: () => void;
  /** Le serveur ne connait pas ce flux (404/405) : capacite absente, pas une panne. */
  surNonPrisEnCharge?: () => void;
  /** Flux coupe, nouvelle tentative programmee. */
  surReconnexion?: () => void;
  fetcher?: Fetcher;
  delaiInitialMs?: number;
}

const DELAI_MAX_MS = 10_000;

/** Ouvre le flux et le rouvre tant que la session vit. Rend la fonction d'arret. */
export function abonnerFluxNavigateur(o: OptionsFlux): () => void {
  const fetcher = o.fetcher ?? ((url, init) => fetch(url, init));
  const controleur = new AbortController();
  let arrete = false;
  const delaiInitial = o.delaiInitialMs ?? 1000;
  let delai = delaiInitial;

  const attendre = (ms: number) =>
    new Promise<void>((r) => {
      setTimeout(r, ms);
    });

  const boucle = async () => {
    while (!arrete) {
      try {
        const res = await fetcher(o.streamUrl, {
          signal: controleur.signal,
          cache: "no-store",
          headers: { Accept: "text/event-stream" },
        });
        if (res.status === 401 || res.status === 403) {
          o.surTerminee();
          return;
        }
        if (res.status === 404 || res.status === 405) {
          o.surNonPrisEnCharge?.();
          return;
        }
        if (!res.ok || !res.body) {
          throw new Error(`flux navigateur ${res.status}`);
        }
        delai = delaiInitial;
        const lecteur = res.body.getReader();
        const decodeur = new TextDecoder();
        let tampon = "";
        while (!arrete) {
          const { value, done } = await lecteur.read();
          if (done) {
            break;
          }
          tampon += decodeur.decode(value, { stream: true });
          const { evenements, reste } = analyserTamponSse(tampon);
          tampon = reste;
          for (const e of evenements) {
            o.surMessage(e);
          }
        }
      } catch {
        if (arrete) {
          return;
        }
      }
      if (arrete) {
        return;
      }
      // Le flux s'est arrete : fin de session (jeton expire) ou simple coupure ?
      const etat = await lireEtatNavigateur(
        o.etatUrl,
        fetcher,
        controleur.signal
      );
      if (arrete) {
        return;
      }
      if (etat === "terminee") {
        o.surTerminee();
        return;
      }
      o.surReconnexion?.();
      await attendre(delai);
      delai = Math.min(delai * 2, DELAI_MAX_MS);
    }
  };

  void boucle();
  return () => {
    arrete = true;
    controleur.abort();
  };
}
